const API_BASE = '/api';

class ApiClient {
  private accessToken: string | null = null;
  private refreshingPromise: Promise<boolean> | null = null;
  private onAuthExpired: (() => void) | null = null;

  setAccessToken(token: string | null): void {
    this.accessToken = token;
  }

  getAccessToken(): string | null {
    return this.accessToken;
  }

  clearTokens(): void {
    this.accessToken = null;
  }

  /**
   * Registra callback para ser chamado quando o refresh falhar e a sessão
   * for considerada perdida. useAuth usa isso para setUser(null) — o
   * RequireAuth/AppLayout então renderiza <Navigate to="/login"> via
   * React Router, sem hard reload (que perdia cache + state + Sentry session).
   */
  setOnAuthExpired(cb: (() => void) | null): void {
    this.onAuthExpired = cb;
  }

  private async request<T>(path: string, options: RequestInit = {}, isRetry = false): Promise<T> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...((options.headers as Record<string, string>) || {}),
    };

    if (this.accessToken) {
      headers['Authorization'] = `Bearer ${this.accessToken}`;
    }

    const response = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers,
      credentials: 'include',
    });

    // O 401 de /auth/login não é sessão vencida, é credencial errada: não há
    // sessão para renovar. Tratar os dois no mesmo caminho fazia a tela dizer
    // "Sessão expirada" para quem só tinha digitado a senha errada, e mandou
    // uma investigação inteira para o lado errado em 25/08/2026.
    const ehTentativaDeEntrar = path.startsWith('/auth/login');

    if (response.status === 401 && !isRetry && !ehTentativaDeEntrar) {
      const refreshed = await this.tryRefresh();
      if (refreshed) {
        return this.request<T>(path, options, true);
      }
      // Refresh falhou — limpa estado e dispara onAuthExpired (caller decide
      // como reagir; em geral useAuth setUser(null) e Router navega).
      this.clearTokens();
      try { this.onAuthExpired?.(); } catch { /* não quebra request */ }
      throw new Error('Sessão expirada');
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Erro desconhecido' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    return response.json();
  }

  /**
   * Refresh de access token. Usa mutex via promise singleton — múltiplas
   * requests que recebem 401 simultaneamente aguardam a mesma refresh ao
   * invés de disparar N chamadas em paralelo.
   *
   * O singleton é liberado imediatamente após a Promise resolver. Antes
   * havia um setTimeout(0) com a justificativa "evitar corrida no happy
   * path" — mas como o backend agora detecta replay (mig 033), qualquer
   * race entre dois /refresh dispara revoke da family. Liberar imediato é
   * estritamente correto.
   */
  async tryRefresh(): Promise<boolean> {
    if (this.refreshingPromise) {
      return this.refreshingPromise;
    }

    this.refreshingPromise = (async () => {
      try {
        const response = await fetch(`${API_BASE}/auth/refresh`, {
          method: 'POST',
          credentials: 'include',
        });

        if (!response.ok) return false;

        const data = await response.json();
        if (!data?.accessToken) return false;

        this.setAccessToken(data.accessToken);
        return true;
      } catch {
        return false;
      } finally {
        this.refreshingPromise = null;
      }
    })();

    return this.refreshingPromise;
  }

  // Auth
  async login(email: string, password: string) {
    const data = await this.request<{ user: import('../types').User; accessToken: string }>(
      '/auth/login',
      { method: 'POST', body: JSON.stringify({ email, password }) }
    );
    this.setAccessToken(data.accessToken);
    return data;
  }

  async getMe() {
    return this.request<import('../types').User>('/auth/me');
  }

  async logout() {
    try {
      return await this.request<{ message: string }>('/auth/logout', { method: 'POST' });
    } finally {
      this.clearTokens();
    }
  }

  async changeEmail(currentPassword: string, newEmail: string) {
    return this.request<{ message: string; revokedSessions: number }>('/auth/change-email', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newEmail }),
    });
  }

  async changePassword(currentPassword: string, newPassword: string) {
    return this.request<{ message: string; revokedSessions: number }>('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  }

  // Clients
  async getClients(opts?: { signal?: AbortSignal }) {
    return this.request<{ clients: import('../types').Client[] }>('/clients', { signal: opts?.signal });
  }

  async getClient(id: string, opts?: { signal?: AbortSignal }) {
    return this.request<import('../types').Client>(`/clients/${id}`, { signal: opts?.signal });
  }

  async createClient(data: { name: string; slug: string; logoUrl?: string }) {
    return this.request<import('../types').Client>('/clients', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateClient(id: string, data: Partial<{ name: string; slug: string; logoUrl: string; active: boolean }>) {
    return this.request<import('../types').Client>(`/clients/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deactivateClient(id: string) {
    return this.request<{ message: string }>(`/clients/${id}`, { method: 'DELETE' });
  }

  async deleteClient(id: string) {
    return this.request<{ message: string }>(`/clients/${id}/delete`, { method: 'DELETE' });
  }

  // Client settings
  async getClientSettings(clientId: string) {
    return this.request<import('../types').ClientSettings>(`/clients/${clientId}/settings`);
  }

  /**
   * Pede ao servidor a URL de consentimento do Google. O redirecionamento em si
   * é feito pelo navegador: se seguíssemos um 302 aqui, o fetch consumiria a
   * tela de login do Google e o operador não veria nada.
   */
  async googleAuthorizeUrl(clientId: string) {
    return this.request<{ url: string; redirectUri: string }>(
      `/clients/${clientId}/google/authorize`
    );
  }

  async updateClientSettings(clientId: string, data: Record<string, unknown>) {
    return this.request<import('../types').ClientSettings>(`/clients/${clientId}/settings`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Client users
  async getClientUsers(clientId: string) {
    return this.request<{ users: Array<{ id: string; user_id: string; email: string; user_name: string; role: string }> }>(
      `/clients/${clientId}/users`
    );
  }

  async alterarPapelDeUsuario(clientId: string, userId: string, role: string) {
    return this.request<{ membership: { role: string } }>(`/clients/${clientId}/users/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify({ role }),
    });
  }

  async redefinirSenhaDeUsuario(clientId: string, userId: string, password: string) {
    return this.request<void>(`/clients/${clientId}/users/${userId}/senha`, {
      method: 'POST',
      body: JSON.stringify({ password }),
    });
  }

  async removerUsuarioDoCliente(clientId: string, userId: string) {
    return this.request<void>(`/clients/${clientId}/users/${userId}`, { method: 'DELETE' });
  }

  async inviteUser(clientId: string, data: { email: string; name: string; password: string; role: string }) {
    return this.request(`/clients/${clientId}/users`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // API Keys
  async getApiKeys(clientId: string) {
    return this.request<{ keys: import('../types').ApiKey[] }>(`/clients/${clientId}/api-keys`);
  }

  async createApiKey(clientId: string, data: { name: string; expiresInDays?: number }) {
    return this.request<import('../types').ApiKey & { key: string }>(`/clients/${clientId}/api-keys`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async revokeApiKey(clientId: string, keyId: string) {
    return this.request(`/clients/${clientId}/api-keys/${keyId}`, { method: 'DELETE' });
  }

  // WhatsApp Connection por número
  async getNumberStatus(clientId: string, numberId: string) {
    return this.request(`/clients/${clientId}/whatsapp-numbers/${numberId}/status`);
  }

  async getAllNumbersStatus(clientId: string) {
    return this.request<{ statuses: Record<string, { status: string; connected: boolean; phoneNumber?: string; profileName?: string; connectionType?: 'baileys' | 'cloud_api' }> }>(
      `/clients/${clientId}/whatsapp-numbers/status-all`
    );
  }

  async connectNumber(clientId: string, numberId: string) {
    return this.request(`/clients/${clientId}/whatsapp-numbers/${numberId}/connect`, { method: 'POST' });
  }

  async connectNumberCloudApi(clientId: string, numberId: string, code: string) {
    return this.request<{
      status: string;
      phoneNumber?: string;
      connectionType: 'cloud_api';
      adAccounts: import('../types').MetaAdAccount[];
      adAccountVinculada: string | null;
      avisoAdsRead: string | null;
    }>(`/clients/${clientId}/whatsapp-numbers/${numberId}/cloud-api/connect`, {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  }

  /**
   * Reprocessa a atribuição de leads antigos que ficaram sem campanha.
   * `simular` conta sem enfileirar, para saber o tamanho antes de disparar.
   */
  async reenriquecerLeads(clientId: string, opcoes: { simular?: boolean; limite?: number } = {}) {
    const params = new URLSearchParams();
    if (opcoes.simular) params.set('simular', '1');
    if (opcoes.limite) params.set('limite', String(opcoes.limite));
    const query = params.toString();

    return this.request<{
      simulado?: boolean;
      encontrados: number;
      enfileirados?: number;
      porCaminho: { ad_id: number; sourceUrl: number; gclid: number };
    }>(`/admin/clients/${clientId}/reenriquecer${query ? `?${query}` : ''}`, { method: 'POST' });
  }

  async metaVincularAdAccount(clientId: string, adAccountId: string) {
    return this.request<{
      vinculada: string;
      pixels: { id: string; name: string | null; lastFiredTime: string | null }[];
      pixelVinculado: string | null;
    }>(`/clients/${clientId}/meta/ad-account`, {
      method: 'POST',
      body: JSON.stringify({ adAccountId }),
    });
  }

  async metaAdsConsent(clientId: string, code: string, redirectUri: string) {
    return this.request<{
      adAccounts: import('../types').MetaAdAccount[];
      vinculada: string | null;
      pixels?: { id: string; name: string | null; lastFiredTime: string | null }[];
      pixelVinculado?: string | null;
    }>(
      `/clients/${clientId}/meta/ads-consent`,
      { method: 'POST', body: JSON.stringify({ code, redirectUri }) }
    );
  }

  async getMetaAdAccounts(clientId: string) {
    return this.request<{
      adAccounts: import('../types').MetaAdAccount[];
      vinculada: string | null;
      avisoAdsRead: string | null;
    }>(`/clients/${clientId}/meta/ad-accounts`);
  }

  async getGoogleAdAccounts(clientId: string) {
    return this.request<{
      contas: import('../types').GoogleAdAccount[];
      vinculada: string | null;
      aviso: string | null;
    }>(`/clients/${clientId}/google/ad-accounts`);
  }

  async getGoogleConversionActions(clientId: string, customerId: string) {
    const busca = new URLSearchParams({ customerId });
    return this.request<{
      acoes: import('../types').GoogleConversionAction[];
      vinculada: string | null;
      aviso: string | null;
    }>(`/clients/${clientId}/google/conversion-actions?${busca.toString()}`);
  }

  async setMetaAdAccount(clientId: string, accountId: string) {
    return this.request<import('../types').ClientSettings>(`/clients/${clientId}/settings`, {
      method: 'PUT',
      body: JSON.stringify({ metaAdAccountId: accountId }),
    });
  }

  async disconnectNumber(clientId: string, numberId: string) {
    return this.request(`/clients/${clientId}/whatsapp-numbers/${numberId}/disconnect`, { method: 'POST' });
  }

  async getWhatsappNumbers(clientId: string) {
    return this.request<{ numbers: import('../types').WhatsappNumber[] }>(`/clients/${clientId}/whatsapp-numbers`);
  }

  async createWhatsappNumber(clientId: string, data: { name: string; phone: string; isDefault?: boolean }) {
    return this.request<import('../types').WhatsappNumber>(`/clients/${clientId}/whatsapp-numbers`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateWhatsappNumber(clientId: string, numberId: string, data: { name?: string; phone?: string; active?: boolean }) {
    return this.request<import('../types').WhatsappNumber>(`/clients/${clientId}/whatsapp-numbers/${numberId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteWhatsappNumber(clientId: string, numberId: string) {
    return this.request(`/clients/${clientId}/whatsapp-numbers/${numberId}`, { method: 'DELETE' });
  }

  async setDefaultWhatsappNumber(clientId: string, numberId: string) {
    return this.request<import('../types').WhatsappNumber>(`/clients/${clientId}/whatsapp-numbers/${numberId}/set-default`, {
      method: 'PUT',
    });
  }

  // Integration Script
  async getIntegrationScript(clientId: string) {
    return this.request<{
      clientName: string;
      clientSlug: string;
      trackerUrl: string;
      numbers: { name: string; phone: string; isDefault: boolean }[];
    }>(`/clients/${clientId}/integration-script`);
  }

  // Leads
  async getLeads(clientId: string, params: Record<string, string | number> = {}, opts?: { signal?: AbortSignal }) {
    const query = new URLSearchParams();
    // `if (v)` antigo pulava 0 e '' como falsy — page=0 nunca chegava ao
    // backend, gerando comportamento inconsistente. Agora aceita 0 e
    // strings explícitas, só descarta undefined/null/''.
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') query.set(k, String(v));
    });
    const qs = query.toString();
    return this.request<import('../types').LeadsResponse>(
      `/clients/${clientId}/leads${qs ? `?${qs}` : ''}`,
      { signal: opts?.signal }
    );
  }

  async getLead(clientId: string, leadId: string, opts?: { signal?: AbortSignal }) {
    return this.request<import('../types').Lead>(
      `/clients/${clientId}/leads/${leadId}`,
      { signal: opts?.signal }
    );
  }

  async updateLeadStatus(clientId: string, leadId: string, data: { status: string; revenue?: number; notes?: string }) {
    return this.request<import('../types').Lead>(`/clients/${clientId}/leads/${leadId}/status`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async getLeadMessages(clientId: string, leadId: string, opts?: { signal?: AbortSignal }) {
    return this.request<{ messages: import('../types').LeadMessage[]; total?: number }>(
      `/clients/${clientId}/leads/${leadId}/messages`,
      { signal: opts?.signal }
    );
  }

  /**
   * Faz download do CSV de leads como blob, autenticando via Authorization
   * Bearer (não via query string — evita logar o token em access-logs de
   * proxy/CDN). Se der 401, tenta refresh e repete uma vez.
   */
  async downloadLeadsCsv(clientId: string, params: Record<string, string> = {}): Promise<Blob> {
    const query = new URLSearchParams(params);
    const url = `${API_BASE}/clients/${clientId}/leads/export${query.toString() ? `?${query}` : ''}`;

    const doFetch = () =>
      fetch(url, {
        credentials: 'include',
        headers: this.accessToken ? { Authorization: `Bearer ${this.accessToken}` } : {},
      });

    let response = await doFetch();
    if (response.status === 401) {
      const refreshed = await this.tryRefresh();
      if (!refreshed) {
        this.clearTokens();
        if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        throw new Error('Sessão expirada');
      }
      response = await doFetch();
    }

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    return response.blob();
  }

  // Metrics
  async getMetrics(clientId: string, opts?: { signal?: AbortSignal }) {
    return this.request<import('../types').Metrics>(
      `/clients/${clientId}/metrics`,
      { signal: opts?.signal }
    );
  }

  async getCampaigns(clientId: string, opts?: { signal?: AbortSignal; startDate?: string; endDate?: string }) {
    const q = new URLSearchParams();
    if (opts?.startDate) q.set('startDate', opts.startDate);
    if (opts?.endDate) q.set('endDate', opts.endDate);
    const qs = q.toString();
    return this.request<{ campaigns: import('../types').Campaign[]; gastoMeta?: import('../types').GastoMeta }>(
      `/clients/${clientId}/campaigns${qs ? `?${qs}` : ''}`,
      { signal: opts?.signal }
    );
  }

  // AI Summary
  async getLeadAiSummary(clientId: string, leadId: string) {
    return this.request<{ summary: string }>(`/clients/${clientId}/leads/${leadId}/ai-summary`);
  }

  /**
   * Pede a análise e volta na hora com o id, sem esperar a IA.
   *
   * A rota era síncrona e segurava a conexão por dois a três minutos, o que
   * estourava o proxy e derrubava a classificação de marcos. Agora o servidor
   * enfileira e a tela acompanha com `buscarAnaliseComercial`.
   */
  async iniciarAnaliseComercial(
    clientId: string,
    params: Record<string, string> = {},
    opts?: { signal?: AbortSignal }
  ) {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') query.set(k, String(v));
    });
    const qs = query.toString();
    return this.request<{ analiseId: string; status: 'processando'; reaproveitada?: boolean }>(
      `/clients/${clientId}/leads/commercial-analysis${qs ? `?${qs}` : ''}`,
      { method: 'POST', signal: opts?.signal }
    );
  }

  async buscarAnaliseComercial(
    clientId: string,
    analiseId: string,
    opts?: { signal?: AbortSignal }
  ) {
    return this.request<
      | { status: 'processando'; analiseId: string; iniciadaEm: string }
      | ({ status: 'concluida'; analiseId: string; concluidaEm: string } & import('../types').CommercialAnalysisResponse)
    >(`/clients/${clientId}/leads/commercial-analysis/${analiseId}`, { signal: opts?.signal });
  }

  // Auto Status Rules
  async getAutoRules(clientId: string) {
    return this.request<{ rules: import('../types').AutoStatusRule[] }>(`/clients/${clientId}/auto-rules`);
  }

  async createAutoRule(clientId: string, data: { keywords: string; targetStatus: string; direction: string }) {
    return this.request<import('../types').AutoStatusRule>(`/clients/${clientId}/auto-rules`, {
      method: 'POST', body: JSON.stringify(data),
    });
  }

  async deleteAutoRule(clientId: string, ruleId: string) {
    return this.request(`/clients/${clientId}/auto-rules/${ruleId}`, { method: 'DELETE' });
  }

  // Admin
  async getAdminOverview(opts?: { signal?: AbortSignal }) {
    return this.request<import('../types').AdminOverview>('/admin/overview', { signal: opts?.signal });
  }

  async getAdminUsers(params: { page?: number; limit?: number } = {}, opts?: { signal?: AbortSignal }) {
    const qs = new URLSearchParams();
    if (params.page) qs.set('page', String(params.page));
    if (params.limit) qs.set('limit', String(params.limit));
    const path = `/admin/users${qs.toString() ? `?${qs}` : ''}`;
    return this.request<{
      users: import('../types').User[];
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    }>(path, { signal: opts?.signal });
  }
}

export const api = new ApiClient();
